import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import {
  formatColumnLabel,
  NO_VALUE_OPERATORS,
} from "../hooks/useManageSegmentation";
import { Close } from "@mui/icons-material";

/**
 * Build a human-readable label for the operator.
 * Falls back to the raw operator string if not found in bootData.
 */
const getOperatorLabel = (operators, type, operatorValue) => {
  const list = operators[type] ?? [];
  const match = list.find((op) => op.value === operatorValue);
  return match ? match.label.toLowerCase() : operatorValue.toLowerCase();
};

/**
 * Build the display value portion of the chip label.
 * When dropdownOptions are available for the column, resolve raw values to labels.
 */
const resolveValue = (raw, column, dropdownOptions, asyncDropdownOptions) => {
  const cfg = dropdownOptions?.[column];

  if (cfg) {
    const { options, valueKey } = cfg;
    if (options && valueKey) {
      const match = options.find((o) => String(o[valueKey]) === String(raw));
      if (match) return match.label;
    }
  }
  const asyncCfg = asyncDropdownOptions?.[column];
  if (asyncCfg) {
    const { options: asyncOpts, valueKey: asyncVk } = asyncCfg;
    if (asyncOpts && asyncVk) {
      const match = asyncOpts.find((o) => String(o[asyncVk]) === String(raw));
      if (match) return match.label;
    }
  }
  return String(raw ?? "");
};

const formatDisplayValue = (
  operator,
  value,
  column,
  dropdownOptions,
  asyncDropdownOptions,
) => {
  if (NO_VALUE_OPERATORS.has(operator)) return { text: "", extra: 0 };

  if (operator === "BETWEEN") {
    const pair = Array.isArray(value) ? value : ["", ""];
    return { text: `${pair[0] || "?"} - ${pair[1] || "?"}`, extra: 0 };
  }

  if (operator === "IN" || operator === "NOT IN") {
    const tags = Array.isArray(value) ? value : value ? [value] : [];
    const resolved = tags.map((t) =>
      resolveValue(t, column, dropdownOptions, asyncDropdownOptions),
    );
    if (resolved.length === 0) return { text: "", extra: 0 };
    if (resolved.length === 1) return { text: resolved[0], extra: 0 };
    return { text: resolved[0], extra: resolved.length - 1 };
  }

  return {
    text: resolveValue(value, column, dropdownOptions, asyncDropdownOptions),
    extra: 0,
  };
};

export const CriteriaChips = ({
  rules,
  operators,
  dropdownOptions,
  asyncDropdownOptions,
  onEditRule,
  onRemoveRule,
  allCriteria,
}) => {
  if (!rules || rules.length === 0) return null;

  const isInteractive = Boolean(onEditRule || onRemoveRule);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      {rules.map((rule, index) => {
        const columnLabel = formatColumnLabel(rule, allCriteria);
        const opLabel = getOperatorLabel(operators, rule.type, rule.operator);
        const { text: displayValue, extra } = formatDisplayValue(
          rule.operator,
          rule.value,
          rule.column,
          dropdownOptions,
          asyncDropdownOptions,
        );

        const chipLabel = (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600, color: "info.main" }}
            >
              {columnLabel}
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              {opLabel}
            </Typography>
            {displayValue && (
              <Typography component="span" variant="body2" fontWeight={500}>
                {rule.column === "product_id"
                  ? Array.isArray(rule?.list) && rule.list.length > 0
                    ? `${rule.list[0].name}`
                    : rule.name || displayValue
                  : displayValue}
              </Typography>
            )}
            {extra > 0 && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "divider",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 500,
                  minWidth: 22,
                  height: 20,
                  px: 0.5,
                  ml: 0.25,
                }}
              >
                +{extra}
              </Box>
            )}
          </Box>
        );

        return (
          <Chip
            key={rule._id ?? index}
            label={
              <Tooltip
                title={isInteractive ? "Edit" : ""}
                arrow
                disableInteractive
              >
                {chipLabel}
              </Tooltip>
            }
            {...(onEditRule && { onClick: () => onEditRule(index) })}
            {...(onRemoveRule && {
              onDelete: () => onRemoveRule(index),
              deleteIcon: (
                <Tooltip title="Remove" arrow disableInteractive>
                  <Close onMouseDown={(e) => e.stopPropagation()} />
                </Tooltip>
              ),
            })}
            variant="outlined"
            sx={{
              borderColor: "divider",
              borderRadius: "8px",
              height: "auto",
              py: 0.75,
              px: 0.5,
              gap: "6px",
              backgroundColor: "#1560D40A",
              cursor: isInteractive ? "pointer" : "default",
              "& .MuiChip-label": {
                px: 1,
              },
              ...(isInteractive && {
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }),
              "& .MuiChip-deleteIcon": {
                fontSize: 16,
              },
            }}
          />
        );
      })}
    </Box>
  );
};
